/**
 * Portal strings (Danish, du-form). Same keys as en.ts.
 * TODO(native review): written by the implementing engineer; a native-speaking UX writer should review tone and
 * terminology before the first Danish release (HANDOFF §2.3).
 */
export const da: Record<string, string> = {
  "p.you": "Dig",

  // ---- navigation and shell
  "p.nav.home": "Forside",
  "p.nav.tasks": "Opgaver",
  "p.nav.request": "Anmod om adgang",
  "p.nav.requestShort": "Anmod",
  "p.nav.myAccess": "Min adgang",
  "p.nav.myRequests": "Mine anmodninger",
  "p.nav.reviews": "Adgangsgennemgange",
  "p.nav.team": "Mit team",
  "p.nav.servicedesk": "Servicedesk",
  "p.nav.profile": "Profil og stedfortræder",
  "p.menu.account": "Kontomenu for {name}",
  "p.menu.language": "Sprog",

  // ---- shared
  "p.people.placeholder": "Søg på navn eller e-mail",
  "p.people.noMatches": "Ingen matcher det",
  "p.people.minChars": "Skriv mindst 2 tegn",
  "p.error.badge": "Kunne ikke indlæses",
  "p.error.loadTitle": "Siden kunne ikke indlæses",
  "p.error.loadTodo": "Prøv igen om lidt. Sker det igen, så fortæl it-support, hvad du var i gang med.",
  "p.error.retry": "Prøv igen",
  "p.noIdentity.title": "Din konto er ikke knyttet til en person endnu",
  "p.noIdentity.body": "Min adgang, Mine anmodninger og Mit team kræver den kobling. Godkendelser og servicedesk virker uden. Bed it-support om at koble din konto.",
  "p.noIdentity.short": "Ikke tilgængelig, før din konto er knyttet til en person.",
  "p.sod.otherSide": "den anden side af reglen ”{policy}“",
  "p.action.grant": "Ny adgang",
  "p.action.revoke": "Fjernelse",
  "p.action.extend": "Forlængelse",
  "p.approverKind.manager": "leder",
  "p.approverKind.role_owner": "rolleejer",
  "p.approverKind.system_owner": "systemejer",
  "p.approverKind.entitlement_owner": "ejer",
  "p.approverKind.user": "godkender",
  "p.approverKind.group": "gruppe",

  // ---- home
  "p.home.morning": "Godmorgen, {name}",
  "p.home.afternoon": "Goddag, {name}",
  "p.home.evening": "Godaften, {name}",
  "p.home.nothingWaiting": "Der er ikke noget, der venter på dig",
  "p.home.nothingWaitingBody": "Godkendelser, adgangsgennemgange og anmodninger, der kræver dig, dukker op her.",
  "p.home.oneThing": "1 ting kræver dig.",
  "p.home.things": "{count} ting kræver dig.",
  "p.home.todo": "Det, der kræver dig",
  "p.home.allTasks": "Se alle opgaver",
  "p.home.row.failed": "{count} af dine anmodninger kunne ikke sættes op",
  "p.home.row.failedContext": "En administrator skal se på hvorfor. Se, hvad der skete.",
  "p.home.row.escalated": "{count} godkendelser er sendt videre til dig",
  "p.home.row.escalatedContext": "Den oprindelige godkender nåede ikke at tage stilling.",
  "p.home.row.approval": "1 anmodning venter på din afgørelse",
  "p.home.row.approvals": "{count} anmodninger venter på din afgørelse",
  "p.home.row.approvalsDue": "{count} skal afgøres inden for et døgn",
  "p.home.row.approvalsContext": "Godkend eller afvis dem under Opgaver.",
  "p.home.row.review": "Adgangsgennemgang: {name}",
  "p.home.row.reviewContext": "{left} punkter tilbage · frist {date}",
  "p.home.row.pending": "1 af dine anmodninger er på vej",
  "p.home.row.pendings": "{count} af dine anmodninger er på vej",
  "p.home.row.pendingContext": "Du får en e-mail, når de er afgjort.",
  "p.home.row.team": "{count} åbne anmodninger for folk i dit team",
  "p.home.row.teamContext": "Afventer godkendelse.",
  "p.home.expiring": "Adgang, der snart udløber",
  "p.home.nothingExpires": "Intet udløber de næste 30 dage.",
  "p.home.quick": "Genveje",
  "p.home.requestForMe": "Anmod om adgang til mig selv",
  "p.home.requestForTeam": "Anmod for en i mit team",
  "p.home.seeAccess": "Se, hvad jeg har adgang til",
  "p.home.setDelegation": "Vælg en stedfortræder ved fravær",
  "p.home.counts": "Du har {roles} roller, {entitlements} direkte rettigheder og {accounts} konti.",

  // ---- tasks (inbox)
  "p.tasks.waitOne": "1 ting venter på din afgørelse.",
  "p.tasks.wait": "{count} ting venter på din afgørelse.",
  "p.tasks.waitUrgent": "{count} ting venter på din afgørelse. {urgent} kræver opmærksomhed nu.",
  "p.tasks.healthy": "Intet er blokeret eller over fristen.",
  "p.tasks.emptyBody": "Godkendelser, adgangsgennemgange og anmodninger, der kræver dig, dukker op her.",
  "p.tasks.itemGone": "Punktet er ikke længere i din indbakke.",
  "p.tasks.type.approval": "Godkendelse",
  "p.tasks.type.review": "Adgangsgennemgang",
  "p.tasks.type.request": "Anmodning",
  "p.tasks.source.approvals": "Godkendelser",
  "p.tasks.source.reviews": "Adgangsgennemgange",
  "p.tasks.source.requests": "Anmodninger",
  "p.tasks.for": "til {name}",
  "p.tasks.askedBy": "anmodet af {name}",
  "p.tasks.askedSelf": "anmodet selv",
  "p.tasks.due": "frist {when}",
  "p.tasks.overdue": "over fristen · skulle være afgjort {when}",
  "p.tasks.dueShort": "frist {when}",
  "p.tasks.overdueShort": "over fristen",
  "p.tasks.reviewContext": "{left} af {total} punkter tilbage",
  "p.tasks.reviewLeft": "{left} punkter mangler. Gennemgangen har frist {date}.",
  "p.tasks.openReview": "Åbn gennemgangen",
  "p.tasks.failedTitle": "Anmodning #{n} kunne ikke sættes op",
  "p.tasks.failedBody": "Afgørelserne er truffet, men adgangen til {name} kunne ikke sættes op i mindst ét system.",
  "p.tasks.failedFix": "En administrator skal se på hvorfor. Du behøver ikke gøre noget, men du kan spørge it-support om status.",

  // ---- approval detail (J2)
  "p.approval.wants": "{name} vil have {item}",
  "p.approval.request": "en anmodning",
  "p.approval.asDelegate": "på vegne af {name}",
  "p.approval.askedSelf": "{name} anmodede selv {when}",
  "p.approval.askedBy": "Anmodet af {by} den {when}",
  "p.approval.in": "i {system}",
  "p.approval.lets": "Hvad det giver {name} mulighed for",
  "p.approval.noDescription": "Der er ingen beskrivelse af denne adgang.",
  "p.approval.why": "Hvorfor, med {name}s ord",
  "p.approval.noJustification": "Der er ikke givet en begrundelse.",
  "p.approval.until": "Indtil",
  "p.approval.noEndDate": "Ingen slutdato",
  "p.approval.action": "Type",
  "p.approval.conflictTitle": "Konflikt med adgang, {name} allerede har",
  "p.approval.conflictBody": "Reglen tillader det, men konflikten registreres, og regelejeren får besked. Skriv i kommentaren, hvorfor det er i orden, eller afvis.",
  "p.approval.steps": "Hvem afgør",
  "p.approval.step": "Trin {n}",
  "p.approval.group": "Enhver i {group}",
  "p.approval.unassigned": "Ingen endnu",
  "p.approval.skippedSelf": "anmodede selv",
  "p.approval.comment": "Kommentar",
  "p.approval.commentOptional": "Kommentar (valgfri)",
  "p.approval.commentHint": "Påkrævet ved afvisning. Den, der anmodede, læser den.",
  "p.approval.commentHintConflict": "Påkrævet ved afvisning, anbefalet når du godkender en konflikt. Den, der anmodede, læser den.",
  "p.approval.reason": "Begrundelse",
  "p.approval.reasonRequired": "Skriv, hvorfor du afviser. Den, der anmodede, læser det.",
  "p.approval.approve": "Godkend",
  "p.approval.reject": "Afvis",
  "p.approval.seeAccess": "Se al {name}s adgang",
  "p.approval.openRequest": "Åbn anmodningen",
  "p.approval.approvedToast": "Godkendte {item} til {name}",
  "p.approval.rejectedToast": "Afviste {item} til {name}",

  // ---- bulk approval
  "p.bulk.selectMode": "Vælg flere",
  "p.bulk.select": "Vælg {item}",
  "p.bulk.selected": "{count} valgt",
  "p.bulk.guard": "Kun punkter med lav risiko og uden konflikter kan afgøres samlet. Åbn de andre ét ad gangen.",
  "p.bulk.approveN": "Godkend {count}",
  "p.bulk.rejectN": "Afvis {count}",
  "p.bulk.approveTitle": "Godkend {count} anmodninger?",
  "p.bulk.rejectTitle": "Afvis {count} anmodninger?",
  "p.bulk.openToDecide": "Åbn for at afgøre",
  "p.bulk.someNotDecided": "Nogle punkter skal åbnes, før du kan afgøre dem.",
  "p.bulk.approved": "Godkendt",
  "p.bulk.rejected": "Afvist",
  "p.bulk.approvedToast": "Godkendte {count} anmodninger",
  "p.bulk.rejectedToast": "Afviste {count} anmodninger",

  // ---- request access (J1)
  "p.request.subtitle": "Find det, du eller en i dit team har brug for. Vi beder de rette personer om at godkende.",
  "p.request.for": "Til",
  "p.request.forMe": "Mig",
  "p.request.forTeam": "En i mit team",
  "p.request.forAnyone": "En anden",
  "p.request.change": "Skift",
  "p.request.pickPerson": "Hvem skal have adgang?",
  "p.request.chooseFirst": "Vælg, hvem der skal have adgang, og søg derefter.",
  "p.request.searchPlaceholder": "Søg efter en rolle, et system eller det, du skal kunne",
  "p.request.applications": "Systemer",
  "p.request.allApplications": "Alle systemer",
  "p.request.browseAll": "Vis alle {count}",
  "p.request.fewerApplications": "Færre systemer",
  "p.request.results": "{count} resultater",
  "p.request.noResults": "Intet matcher",
  "p.request.noResultsHint": "Prøv et andet ord, eller fjern systemfilteret.",
  "p.request.nothingPublished": "Der kan ikke anmodes om adgang til denne person endnu.",
  "p.request.noIdentity": "Din konto er ikke knyttet til en person, så du kan ikke anmode til dig selv. It-support kan anmode på dine vegne.",
  "p.request.inRequest": "{count} i din anmodning",
  "p.request.review": "Gennemse anmodning",
  "p.request.panelTitle": "Anmodning til {name}",
  "p.request.forMeTitle": "dig",
  "p.request.panelEmptyTitle": "Din anmodning",
  "p.request.panelEmptyBody": "Vælg noget fra resultaterne. Du begrunder og sender herfra.",
  "p.request.until": "Indtil",
  "p.request.roleDefault": "Rollens standard · {days} dage",
  "p.request.untilDate": "En dato, jeg vælger",
  "p.request.why": "Hvorfor har {name} brug for det?",
  "p.request.whyMe": "Hvorfor har du brug for det?",
  "p.request.whyHint": "Godkenderne læser det. Skriv, hvad opgaven er, ikke bare ”nødvendigt for jobbet“.",
  "p.request.justificationRequired": "Skriv, hvorfor adgangen er nødvendig. Godkenderne læser det.",
  "p.request.whoDecides": "Hvem afgør",
  "p.request.previewUnavailable": "Vises, når anmodningen er sendt.",
  "p.request.autoApprove": "Ingen godkendelse nødvendig. Adgangen sættes op med det samme.",
  "p.request.wouldBeBlocked": "Kan ikke anmodes på grund af reglen ovenfor.",
  "p.request.skippedSelf": "springes over – du anmodede",
  "p.request.dueWithin": "Afgørelse inden for {days} dage.",
  "p.request.emailMe": "Du får en e-mail uanset hvad.",
  "p.request.emailBoth": "Du og {name} får en e-mail uanset hvad.",
  "p.request.send": "Send anmodning",
  "p.request.addAnother": "Tilføj et punkt mere",
  "p.request.sent": "Anmodning sendt",
  "p.request.sentTo": "Anmodning sendt til {name}",
  "p.request.sendFailed": "Anmodningen kunne ikke sendes",

  // ---- catalogue rows
  "p.catalog.request": "Anmod",
  "p.catalog.requestItem": "Anmod om {item}",
  "p.catalog.inRequest": "I din anmodning",
  "p.catalog.removeFrom": "Fjern {item} fra anmodningen",
  "p.catalog.youHave": "Det har du allerede",
  "p.catalog.has": "{name} har det allerede",
  "p.catalog.alreadyRequested": "Allerede anmodet",
  "p.catalog.seeRequest": "Se anmodningen",
  "p.catalog.detectHint": "Der kan stadig anmodes. Godkenderne ser konflikten.",
  "p.catalog.blockedHint": "{name} skal først afgive den anden adgang. Spørg it-support, hvis det er forkert.",

  // ---- my requests and the request page
  "p.requests.subtitle": "Anmodninger, du har sendt, og anmodninger sendt til dig.",
  "p.requests.one": "Anmodning",
  "p.requests.number": "Anmodning #{n}",
  "p.requests.notFound": "Anmodningen findes ikke, eller du må ikke se den.",
  "p.requests.col.access": "Adgang",
  "p.requests.col.for": "Til",
  "p.requests.col.status": "Status",
  "p.requests.col.sent": "Sendt",
  "p.requests.search": "Søg på adgang eller anmodningsnummer",
  "p.requests.allStates": "Alle",
  "p.requests.approval": "godkendelse",
  "p.requests.scope": "Vis",
  "p.requests.mine": "Mine",
  "p.requests.team": "Mit teams",
  "p.requests.emptyTitle": "Du har ikke anmodet om noget endnu",
  "p.requests.emptyBody": "Søg i kataloget, skriv hvorfor du har brug for det, og de rette personer bliver bedt om at godkende.",
  "p.requests.askedBy": "Anmodet af",
  "p.requests.why": "Hvorfor",
  "p.requests.waiting": "{name} har den.",
  "p.requests.waitingUntil": "{name} har den og skal svare senest {date}.",
  "p.requests.beingSetUp": "Alle sagde ja. Adgangen sættes op nu.",
  "p.requests.ready": "Adgangen er på plads.",
  "p.requests.rejectedBy": "{name} sagde nej. Begrundelsen står nedenfor.",
  "p.requests.cancel": "Annuller anmodning",
  "p.requests.keep": "Behold anmodning",
  "p.requests.cancelTitle": "Annuller denne anmodning?",
  "p.requests.cancelBody": "Anmodning #{n} trækkes tilbage, og godkenderne ser den ikke længere. Du kan sende en ny når som helst.",
  "p.requests.cancelled": "Anmodning #{n} annulleret",
  "p.requests.step.sent": "Sendt",
  "p.requests.step.sentDetail": "{when} af {by}",
  "p.requests.step.due": "Frist {date}",
  "p.requests.step.decided": "{when} af {by}",
  "p.requests.step.skippedYou": "Sprunget over – du anmodede selv",
  "p.requests.step.skippedSelf": "Sprunget over – personen anmodede selv",
  "p.requests.step.noApproval": "Ingen godkendelse nødvendig",
  "p.requests.step.ready": "Adgang klar",
  "p.requests.step.willBeReady": "Adgang klar",
  "p.requests.step.autoAfter": "Sættes op automatisk efter godkendelse",
  "p.requests.step.failed": "Kunne ikke sættes op",
  "p.requests.step.notSetUp": "Ikke sat op",

  // ---- my access
  "p.access.subtitle": "Roller, rettigheder og konti, du har lige nu.",
  "p.access.roles": "Roller",
  "p.access.entitlements": "Rettigheder",
  "p.access.accounts": "Konti",
  "p.access.otherRoles": "Andre roller",
  "p.access.noRoles": "Du har ingen roller endnu",
  "p.access.noRolesBody": "Roller, du anmoder om eller får automatisk via dit job, vises her.",
  "p.access.noEntitlements": "Ingen direkte rettigheder",
  "p.access.noEntitlementsBody": "Adgang, der følger med dine roller, vises under Konti.",
  "p.access.noAccounts": "Ingen konti endnu",
  "p.access.noAccountsBody": "Konti oprettes i systemerne, når adgang sættes op.",
  "p.access.noAccountEntitlements": "Intet på denne konto endnu.",
  "p.access.lastRead": "læst {when}",
  "p.access.viaPlatform": "Via platformen",
  "p.access.noEndDate": "Ingen slutdato",
  "p.access.until": "indtil {date}",
  "p.access.extend": "Forlæng",
  "p.access.remove": "Fjern",
  "p.access.extendTitle": "Behold {item} længere",
  "p.access.currentEnd": "Udløber {date}.",
  "p.access.newEnd": "Ny slutdato",
  "p.access.whyStill": "Hvorfor har du stadig brug for den?",
  "p.access.extendSent": "Anmodning om forlængelse sendt",
  "p.access.expiringTitle": "{count} af dine adgange udløber inden for 30 dage",
  "p.access.expiringBody": "Brug Forlæng på rækken for at beholde den. Ellers fjernes den på slutdatoen.",
  "p.access.conflictsTitle": "{count} af dine adgange er i konflikt med hinanden",
  "p.access.conflictsBody": "Regelejeren har fået besked. Du behøver ikke gøre noget, medmindre de kontakter dig.",

  // ---- access review (J3)
  "p.review.listSubtitle": "Tjek, at folk stadig har brug for det, de har.",
  "p.review.none": "Ingen gennemgange venter på dig",
  "p.review.noneBody": "Du får en e-mail, når en gennemgang kræver dig.",
  "p.review.type.manager": "Dit team",
  "p.review.type.role_owner": "Roller, du ejer",
  "p.review.type.system_owner": "Systemer, du ejer",
  "p.review.type.entitlement_owner": "Rettigheder, du ejer",
  "p.review.asDelegate": "som stedfortræder",
  "p.review.progress": "{decided} af {total} afgjort",
  "p.review.left": "{count} tilbage",
  "p.review.continue": "Fortsæt gennemgangen",
  "p.review.view": "Se afgørelser",
  "p.review.title": "Adgangsgennemgang: {name}",
  "p.review.purpose": "Tjek, at hver person stadig har brug for det, de har.",
  "p.review.dueSentence": "Frist {date}.",
  "p.review.removedAtClose": "Adgang, du fjerner, bliver fjernet, når gennemgangen lukker.",
  "p.review.noItems": "Intet at gennemgå",
  "p.review.noItemsBody": "Der er ingen punkter til dig i denne gennemgang.",
  "p.review.people": "Personer",
  "p.review.yourTeam": "Dit team · {count} personer",
  "p.review.peopleCount": "{count} personer",
  "p.review.pickPerson": "Vælg en person for at se vedkommendes adgang.",
  "p.review.done": "Færdig",
  "p.review.keepLowRisk": "Behold {count} automatiske punkter med lav risiko",
  "p.review.keepN": "Behold {count} punkter",
  "p.review.decideOneByOne": "Afgør ét ad gangen",
  "p.review.bulkBody": "Adgang, {name} får automatisk via jobbet, med lav eller mellem risiko og uden konflikt, markeres som beholdt. Du kan ændre hver enkelt bagefter.",
  "p.review.bulkKept": "Beholdt {count} punkter",
  "p.review.bulkKeptPartial": "Beholdt {count} punkter · {failed} kunne ikke afgøres",
  "p.review.conflict": "Konflikt · {count}",
  "p.review.conflictNote": "{name} har {count} åbne konflikter mellem adgange. Se på punkterne med høj risiko først.",
  "p.review.account": "Konto",
  "p.review.entitlements": "{count} rettigheder",
  "p.review.decisionFor": "Afgørelse for {item}",
  "p.review.keep": "Behold",
  "p.review.revoke": "Fjern",
  "p.review.passOn": "Send videre",
  "p.review.assignOwner": "Vælg ejer",
  "p.review.kept": "Beholdt {item}",
  "p.review.revoked": "Markerede {item} til fjernelse",
  "p.review.passedOn": "Sendt videre til {name}",
  "p.review.ownerAssigned": "{name} er foreslået som ejer",
  "p.review.ownerLabel": "Kontoejer",
  "p.review.ownerHint": "Kontoen knyttes til denne person.",
  "p.review.reviewerLabel": "Ny gennemgående",
  "p.review.reviewerHint": "Punktet flytter til denne persons opgaver.",
  "p.review.personDone": "{name} er færdig",
  "p.review.summary": "{kept} beholdt, {revoked} fjernet, {passed} sendt videre.",
  "p.review.removedWhen": "Fjernes, når gennemgangen lukker, {date}",
  "p.review.canChange": "Du kan ændre enhver afgørelse, indtil gennemgangen lukker.",
  "p.review.personProgress": "{decided} af {total} afgjort for {name}",
  "p.review.savedAsYouGo": "Afgørelser gemmes løbende",
  "p.review.next": "Næste: {name} · {left} tilbage",
  "p.review.allDone": "Alt afgjort – tilbage til Opgaver",

  // ---- my team
  "p.team.subtitle": "Dem, der refererer til dig, deres adgang og det, der kræver din opmærksomhed.",
  "p.team.none": "Ingen refererer til dig",
  "p.team.col.person": "Person",
  "p.team.col.access": "Adgang",
  "p.team.col.attention": "Kræver opmærksomhed",
  "p.team.access": "{roles} roller · {accounts} konti",
  "p.team.pending": "{count} anmodninger på vej",
  "p.team.toReview": "{count} at gennemgå",
  "p.team.disabled": "{count} konti deaktiveret",
  "p.team.violationsTitle": "{name} har {count} åbne konflikter mellem adgange",
  "p.team.pendingTitle": "{count} anmodninger til {name} er på vej",
  "p.team.seeRequests": "Se anmodningerne",
  "p.team.removeTitle": "Fjern {item}?",
  "p.team.keepAccess": "Behold adgang",
  "p.team.removeBody": "{item} fjernes fra {name} med det samme. Det registreres som en anmodning, du har godkendt.",
  "p.team.removed": "Fjernede {item} fra {name}",

  // ---- service desk (J6)
  "p.sd.subtitle": "Find en person for at låse konti op, nulstille adgangskoder eller anmode om adgang for vedkommende.",
  "p.sd.searchPlaceholder": "Navn, e-mail, brugernavn eller medarbejdernummer",
  "p.sd.searchHint": "Nok til at finde den rigtige person: medarbejdernummeret er entydigt.",
  "p.sd.person": "1 person",
  "p.sd.people": "{count} personer",
  "p.sd.noMatch": "Ingen matcher det",
  "p.sd.noMatchBody": "Prøv medarbejdernummeret eller e-mailadressen.",
  "p.sd.notFound": "Personen findes ikke eller er fratrådt.",
  "p.sd.accounts": "Konti",
  "p.sd.noAccounts": "Ingen konti i noget system.",
  "p.sd.col.account": "Konto",
  "p.sd.col.status": "Status",
  "p.sd.col.lastRead": "Sidst læst fra systemet",
  "p.sd.col.actions": "Handlinger",
  "p.sd.noActions": "Ingen handlinger",
  "p.sd.changePending": "Ændring på vej",
  "p.sd.unlock": "Lås op",
  "p.sd.reset": "Nulstil adgangskode",
  "p.sd.verifyFirst": "Bekræft først, hvem du taler med",
  "p.sd.beforeTitle": "Før du ændrer noget",
  "p.sd.verified": "Jeg har tjekket, hvem jeg taler med",
  "p.sd.verifyHint": "Spørg om medarbejdernummer ({id}) og lederens navn ({manager}).",
  "p.sd.noResetPermission": "Din rolle kan se, men ikke låse op eller nulstille. Spørg en administrator.",
  "p.sd.otherHelp": "Anden hjælp",
  "p.sd.requestFor": "Anmod om adgang for {name}",
  "p.sd.seeAccess": "Se {name}s adgang",
  "p.sd.hideAccess": "Skjul {name}s adgang",
  "p.sd.accessOf": "{name}s adgang",
  "p.sd.portalLogin": "Portallogin",
  "p.sd.loginDisabled": "Deaktiveret",
  "p.sd.lastLogin": "Sidst logget ind {when}",
  "p.sd.neverLoggedIn": "Aldrig logget ind",
  "p.sd.noLogin": "Intet portallogin",
  "p.sd.recentRequests": "Seneste anmodninger",
  "p.sd.resetTitle": "Nulstil adgangskode i {system}",
  "p.sd.resetBody": "Der laves en ny adgangskode til {account}. Du læser den op for {name}; den vises én gang og gemmes ikke.",
  "p.sd.mustChange": "Skal vælge en ny adgangskode ved næste login",
  "p.sd.alsoUnlock": "Lås også kontoen op",
  "p.sd.resetConfirm": "Nulstil adgangskode",
  "p.sd.resetFailed": "Adgangskoden kunne ikke nulstilles",
  "p.sd.newPasswordFor": "Ny adgangskode til {account}",
  "p.sd.done": "Udført · {time}",
  "p.sd.stage.queued": "I kø",
  "p.sd.stage.sent": "Sendt til {system}",
  "p.sd.stage.confirmed": "Bekræftet af {system}",
  "p.sd.stageShort.queued": "I kø",
  "p.sd.stageShort.sent": "Sendt til {system}",
  "p.sd.stageShort.confirmed": "Udført i {system} · {time}",
  "p.sd.stageShort.retrying": "Prøver igen",
  "p.sd.stageShort.failed": "Fejlet",
  "p.sd.stageShort.cancelled": "Annulleret",
  "p.sd.retrying": "Forsøg {attempts} fejlede – platformen prøver igen kl. {next}. Seneste fejl: {error}",
  "p.sd.failedAction": "Platformen har opgivet. Seneste fejl: {error}. Administratorerne har fået besked.",
  "p.sd.nextTry": "næste forsøg kl. {next}",
  "p.sd.recentActions": "Seneste handlinger (24 timer)",
  "p.sd.op.set_password": "Nulstilling af adgangskode",
  "p.sd.op.enable": "Oplåsning / aktivering",
  "p.sd.op.disable": "Deaktivering",
  "p.sd.op.delete": "Sletning",
  "p.sd.op.create_account": "Opret konto",
  "p.sd.op.update_account": "Opdater konto",
  "p.sd.op.add_entitlement": "Tildel adgang",
  "p.sd.op.remove_entitlement": "Fjern adgang",
  "p.sd.unlocked": "Kontoen er låst op",
  "p.sd.mustChangeNote": "{name} skal vælge en ny adgangskode ved login",
  "p.sd.noChangeNote": "{name} kan blive ved med at bruge denne adgangskode",
  "p.sd.shownOnce": "Vises én gang",
  "p.sd.shownOnceShort": "Adgangskoden vises én gang og gemmes ikke.",
  "p.sd.shownOnceBody": "Den gemmes ikke og kommer ikke i revisionsloggen. Læs den op for {name} nu, eller send den gennem en kanal, personen stoler på.",
  "p.sd.givenTo": "Jeg har givet den til {name}",
  "p.sd.handedOver": "Adgangskoden er givet til {name}. Den er væk fra skærmen.",
  "p.sd.unlockTitle": "Lås {account} op?",
  "p.sd.keepLocked": "Behold låst",
  "p.sd.unlockBody": "Kontoen i {system} låses op. {name} kan logge ind igen med den adgangskode, personen allerede har.",
  "p.sd.unlockQueued": "Oplåsning på vej for {name}",

  // ---- profile & delegation
  "p.profile.me": "Om dig",
  "p.profile.none": "Ingen profil",
  "p.profile.name": "Navn",
  "p.profile.email": "E-mail",
  "p.profile.userName": "Brugernavn",
  "p.profile.employeeId": "Medarbejdernummer",
  "p.profile.type": "Type",
  "p.profile.jobTitle": "Stilling",
  "p.profile.department": "Afdeling",
  "p.profile.manager": "Leder",
  "p.profile.orgUnit": "Enhed",
  "p.profile.directReports": "Personer, der refererer til dig",
  "p.profile.validTo": "Ansat indtil",
  "p.profile.delegation": "Stedfortræder ved fravær",
  "p.profile.delegationBody": "Mens du er væk, kan en anden afgøre dine godkendelser og adgangsgennemgange.",
  "p.profile.newDelegation": "Tilføj stedfortræder",
  "p.profile.iDelegateTo": "Jeg uddelegerer til",
  "p.profile.delegatedToMe": "Uddelegeret til mig",
  "p.profile.noDelegations": "Ingen stedfortrædere lige nu.",
  "p.profile.nobodyDelegated": "Ingen har uddelegeret til dig.",
  "p.profile.untilRemoved": "indtil den fjernes",
  "p.profile.active": "Aktiv",
  "p.profile.scheduled": "Starter senere",
  "p.profile.ended": "Afsluttet",
  "p.profile.remove": "Fjern",
  "p.profile.delegateTo": "Uddeleger til",
  "p.profile.scopeLabel": "Hvad de må afgøre",
  "p.profile.scope.all": "Godkendelser og adgangsgennemgange",
  "p.profile.scope.approvals": "Kun godkendelser",
  "p.profile.scope.certifications": "Kun adgangsgennemgange",
  "p.profile.from": "Fra",
  "p.profile.until": "Indtil",
  "p.profile.untilHint": "Lad stå tomt for ingen slutdato",
  "p.profile.createDelegation": "Tilføj stedfortræder",
  "p.profile.delegated": "{name} kan nu afgøre for dig",
  "p.profile.removeTitle": "Fjern denne stedfortræder?",
  "p.profile.keepDelegation": "Behold stedfortræder",
  "p.profile.removeBody": "{name} kan ikke længere afgøre på dine vegne.",
  "p.profile.removedToast": "Stedfortræder fjernet",
  "p.profile.changePassword": "Skift adgangskode",
  "p.profile.mustChange": "Vælg en ny adgangskode, før du fortsætter.",
  "p.profile.currentPw": "Nuværende adgangskode",
  "p.profile.newPw": "Ny adgangskode",
  "p.profile.pwHint": "Mindst 12 tegn med et tal og et stort bogstav",
  "p.profile.confirmPw": "Gentag den nye adgangskode",
  "p.profile.pwMismatch": "De to adgangskoder er ikke ens.",
  "p.profile.pwChanged": "Adgangskoden er skiftet",
};
